import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, DrumIcon, FileTextIcon, FolderSearchIcon, LayersIcon, LayoutGridIcon, MessageSquareIcon, MusicIcon, UsersIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
export const PRODUCT_IMAGE = 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/ec175633-6df8-4ed9-ae67-c7c09deb78ea/1792774445209_dr-mockup-landing-fee78531.webp';
// Realistic studio scenes (Higgsfield, WebP in Impultienda Recursos).
export const SCENE_IMAGE_KID = 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/ec175633-6df8-4ed9-ae67-c7c09deb78ea/1792774332992_dr-landing-scene-kid-8db09f1d.webp';
export const SCENE_IMAGE_ADULT = 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/ec175633-6df8-4ed9-ae67-c7c09deb78ea/1792774337226_dr-landing-scene-adult-d711db3b.webp';
export const heroGuarantees = ['Instant digital download', 'One-time payment', 'No subscription', '7-day money-back guarantee'];
export const stats = [{
  value: '240',
  label: 'Pages in the main volumes'
}, {
  value: '6',
  label: 'Digital volumes'
}, {
  value: '100+',
  label: 'Original grooves & exercises'
}, {
  value: '5',
  label: 'Bonuses included'
}, {
  value: '3',
  label: 'Worksheet levels'
}];
export const differenceStats = [{
  value: 'Ages 6 to adult',
  label: 'Young beginners, older beginners, teens & adults'
}, {
  value: '6 digital volumes',
  label: 'From your studio year plan and rudiments to reading, grooves, kids and assessment'
}, {
  value: 'Print-ready PDF',
  label: 'Use it on your tablet or print it for the music stand next to the kit'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Sunday nights disappear',
  text: 'Hours at the computer planning next week’s lessons – and you’re still not done.'
}, {
  title: '“What do I teach this week?”',
  text: 'Every student is at a different level, and you rebuild a plan for each one from scratch.'
}, {
  title: 'Students who don’t practice',
  text: 'No kit at home, worried about the noise – so they come back with the same groove and the same mistakes.'
}, {
  title: 'A 7-year-old with a 10-minute attention span',
  text: 'Half the lesson goes to keeping them focused instead of teaching them to play.'
}, {
  title: 'Parents asking about progress',
  text: 'You know your students are growing, but it’s hard to show it clearly on paper.'
}, {
  title: 'Improvising behind the kit',
  text: 'When there’s no time to prepare, the lesson turns into “let’s just play that beat again.”'
}];
export const afterItems = ['A 36-week studio year plan for 3 age tracks', '12 lessons planned minute by minute (30 / 45 / 60 min)', 'Materials list and teacher script for every lesson', 'Worksheets in 3 levels with answer keys', 'A leveled technique & rudiment method from Pre-Kit to Advanced Beginner', 'Reading and rhythm step by step with groove charts', '100+ original grooves & exercises across styles', 'Progress reports, rubrics and 100 ready-made comments', 'Print-ready PDF'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Your evenings go to lesson planning',
  text: 'Do you spend nights and weekends figuring out what each student should work on next?'
}, {
  icon: FolderSearchIcon,
  title: 'You never know which material to use',
  text: 'Are you always searching online for grooves, rudiment sheets and play-alongs that fit each student’s level?'
}, {
  icon: UsersIcon,
  title: 'Your students are all at different levels',
  text: 'A 7-year-old beginner at 4 pm, a teen rock fan at 5 pm and an adult returner at 6 pm?'
}, {
  icon: DrumIcon,
  title: 'Practice doesn’t happen at home',
  text: 'Do you need a simple practice-pad system that works without a kit – and without bothering the neighbors?'
}, {
  icon: MessageSquareIcon,
  title: 'Parents want to see progress',
  text: 'Would you like clear reports and comments that show families how far their child has come?'
}, {
  icon: LayoutGridIcon,
  title: 'You need a clearer structure',
  text: 'Would you like everything in one place, so you find the right lesson in minutes?'
}];

// Spine colours mirror the physical box (blue, light blue, green, yellow, orange, red).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Volume 01',
  level: 'Ages 6+',
  title: 'Studio Year Plan & 12 Ready-to-Teach Lessons',
  tagline: 'Know exactly what to teach every week of the studio year.',
  intro: 'The first volume gives you a complete studio year plan and twelve lessons you can teach right away.',
  items: ['36-week studio year plan', '3 tracks: Young Beginners 6–9 · Older Beginners 10–14 · Teens & Adults', '12 lessons planned minute by minute', '30, 45 and 60-minute lesson formats', 'Materials list and teacher script for each lesson', 'Lesson-note template and progression table', 'Test A/B with answer key and assessment matrix'],
  outro: 'Ready-to-teach lessons, so you sit down behind the kit with a clear plan.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volume 02',
  level: 'Ages 6+',
  title: 'Technique & Rudiments: The Leveled Method',
  tagline: 'A clear technical path from the very first lesson.',
  intro: 'The second volume maps six levels and shows you what to teach at each one – and how to check it.',
  items: ['6-level map: Pre-Kit · Level 1 · Level 2 · Level 3 · Level 4 · Advanced Beginner', 'Posture and kit setup', 'Grip: matched, with a traditional-grip intro', 'Stroke types: full, down, tap, up', 'The 40 rudiments in 4 families, sequenced by level', 'Independence, coordination and pedal technique', 'Practice-pad warm-ups and a technique checklist per level', 'Worksheets in 3 levels'],
  outro: 'A simple, consistent method for every student who walks into your studio.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volume 03',
  level: 'Ages 6+',
  title: 'Reading & Rhythm Step by Step',
  tagline: 'Students who read rhythms – not just copy your sticks.',
  intro: 'The third volume teaches drum reading and rhythm in small, logical steps with games and quick drills.',
  items: ['Counting charts and the drum key', 'Quarter notes → eighths → sixteenths → triplets → dotted notes', 'Counting “1 e & a”', 'Reading groove charts', '5-minute sight-reading drills', 'Clapping games', 'Flashcards to print', 'Worksheets in 3 levels + answer keys'],
  outro: 'Reading and rhythm that finally stick, week after week.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volume 04',
  level: 'Ages 6+',
  title: 'Grooves & Styles: 100+ Original Grooves',
  tagline: 'The right groove, at the right level, at the right time.',
  intro: 'The fourth volume gives you original grooves and fills across the styles your students want to play.',
  items: ['Rock, pop, funk, shuffle & blues', 'Jazz swing ride, reggae, hip-hop and 6/8', 'Latin: bossa nova and a songo intro', '1, 2 and 4-beat fills', 'Song structure and original play-along charts', 'How to teach a groove in 4 steps', 'Quizzes + answer keys'],
  outro: 'Grooves students love to play – all original, ready for the lesson.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volume 05',
  level: 'Ages 6+',
  title: 'Teaching Kids & Beginners (and Practice at Home)',
  tagline: 'Short attention spans, no kit at home – solved.',
  intro: 'The fifth volume shows you how to teach young beginners and how to get practice happening at home.',
  items: ['Lessons built in 5-minute blocks', '30 rhythm games', 'Body percussion', 'Quiet practice-pad routines for home', 'A weekly practice system and sticker charts', 'The first 8 lessons for ages 6–9', 'Adapting lessons for adult beginners and online lessons'],
  outro: 'Kids who stay focused – and students who actually practice.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volume 06',
  level: 'Ages 6+',
  title: 'Assessment, Progress Reports & Studio Business',
  tagline: 'Show students and parents what they can do – and what comes next.',
  intro: 'The sixth volume gives you the tools to measure progress fairly, communicate it clearly and run your studio.',
  items: ['Level-up criteria', 'Test A/B per level', 'Rubrics', 'Term progress reports', '100 ready-made lesson & report comments', 'Studio policy, parent communication and showcase planning', 'End-of-year review'],
  outro: 'Clear, professional reporting that parents notice.',
  note: 'DRUM READY™ is an independent teaching resource. It is not affiliated with or endorsed by any exam board, publisher or drum school. All grooves and exercises are original; rudiments are traditional and public domain.',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 digital volumes for your drum studio',
  text: 'A complete teaching system that helps you plan and teach every student, all year long. Each volume has 40 pages of ready-to-teach lessons, worksheets and material for the drum room.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Lessons planned minute by minute',
  text: 'No more blank pages. Clear plans with timings, materials and a teacher script get you started fast.'
}, {
  icon: FileTextIcon,
  title: 'Worksheets in 3 levels',
  text: 'The same concept in three levels, so every student works on it – at their own level.'
}, {
  icon: MusicIcon,
  title: 'Reading, rhythm & grooves',
  text: 'Counting charts, “1 e & a”, groove charts and 100+ original grooves and exercises, step by step.'
}, {
  icon: LayersIcon,
  title: 'A leveled method',
  text: 'Six levels from Pre-Kit to Advanced Beginner, with the 40 rudiments and a technique checklist for each one.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Assessment & reports',
  text: 'Rubrics, Test A/B, term progress reports and 100 ready-made comments with answer keys.'
}, {
  icon: CalendarCheckIcon,
  title: 'Studio year plan',
  text: 'A 36-week plan for three age tracks, so you always know what comes next.'
}];
export const pageTypes = ['Ready-to-teach lessons', 'Worksheets in 3 levels', 'Answer keys', 'Studio year plan'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'First Lesson Perfect Kit',
  text: 'Everything you need to start a new student with confidence – from the first call to the third lesson.',
  items: ['New-student interview', 'Placement test', 'First 3 lessons for each age group', 'Ready to print'],
  benefit: 'Every new student starts on the right foot.'
}, {
  title: 'The Home Practice System',
  text: 'Simple tools that tell students exactly what to practice – and make them want to do it, even without a kit.',
  items: ['Practice trackers', 'Sticker charts', 'Weekly assignment sheets', '30-day practice-pad challenges'],
  benefit: 'Students who come back having practiced.'
}, {
  title: 'Parent Communication Toolkit',
  text: 'Ready-made words for every conversation with families, from the welcome to the progress update.',
  items: ['Welcome letter', 'Studio policy', '40 email & text templates', 'Progress-update scripts'],
  benefit: 'Professional communication in minutes.'
}, {
  title: 'Anti-Cancellation & Make-Up Lesson Kit',
  text: 'Policies and plans that protect your schedule and keep students learning when life gets in the way.',
  items: ['Cancellation policies', 'Make-up lesson plans', 'Online-lesson plans', '12 off-kit lessons (pad, theory, listening)'],
  benefit: 'Fewer lost lessons – and less stress.'
}, {
  title: 'Group Drum Class & Rhythm Games',
  text: 'Fun, ready-to-run activities for group lessons, drum circles and ensembles.',
  items: ['30 group rhythm games', 'Drum circle & ensemble plans', 'Body percussion', 'Ready to print'],
  benefit: 'Group classes your students look forward to.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Open DRUM READY™',
  text: 'Download the package and open the volume that fits your student.'
}, {
  title: 'Pick the level and lesson',
  text: 'Find the right lesson, worksheet or groove for the student behind the kit.'
}, {
  title: 'Prepare in 5 minutes',
  text: 'Read the teacher script and materials list and get what you need ready.'
}, {
  title: 'Print and teach',
  text: 'Print the worksheets or use them on your tablet – and start the lesson with a clear plan.'
}];
export const audienceItems = ['Teach private drum lessons in your own studio or a music school', 'Teach young beginners, teens and adults', 'Want a ready-made studio year plan', 'Have students at many different levels', 'Are tired of planning every lesson from scratch', 'Want clear worksheets with answer keys', 'Want easier, clearer progress reports for parents', 'Want your Sunday nights back'];
export const timeValueTasks = ['Searching online for grooves and rudiment sheets', 'Planning each student’s lesson', 'Making practice charts and flashcards', 'Choosing grooves by level', 'Writing progress reports'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 digital volumes – 240 pages',
  detail: 'Studio year plan, technique & rudiments, reading, grooves & styles, teaching kids and assessment'
}, {
  label: '5 bonuses at no extra cost',
  detail: 'First Lesson Kit, Home Practice System, Parent Toolkit, Make-Up Lesson Kit and Group Rhythm Games'
}, {
  label: 'Ages 6 to adult',
  detail: 'Ready-to-teach lessons, worksheets in 3 levels and answer keys'
}];
export const offerIncludes = ['Instant digital download', 'PDF files you can keep', 'Print-ready', 'No physical product is shipped'];
export const PRICE_AMOUNT = '$19';
export const PRICE_CURRENCY = 'USD';
export const PRICE = `${PRICE_AMOUNT} ${PRICE_CURRENCY}`;
export const PRICE_BEFORE = '$97';

// Only add authentic reviews of DRUM READY. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'How do I get the material?',
  a: 'DRUM READY™ is delivered digitally. Right after your purchase you get access to the PDF files, which you can download and keep. Use them on your computer, tablet or phone and print only the pages you need.'
}, {
  q: 'Which ages and levels does it cover?',
  a: 'Ages 6 to adult, in three tracks: Young Beginners (6–9), Older Beginners (10–14) and Teens & Adults. The leveled method goes from Pre-Kit to Advanced Beginner.'
}, {
  q: 'Is it tied to a specific method book, school or exam?',
  a: 'No. DRUM READY™ is an independent teaching resource and works alongside any method you already use. It is not affiliated with or endorsed by any exam board, publisher or drum school.'
}, {
  q: 'What grooves and music are included?',
  a: 'Only original grooves, fills, exercises and play-along charts, plus the traditional rudiments, which are public domain. No transcriptions of copyrighted songs.'
}, {
  q: 'How is drum notation shown?',
  a: 'Grooves and exercises are written as clear counting charts – hi-hat, snare and bass drum rows over “1 e & a” – with a legend on every page, so even young beginners can read them.'
}, {
  q: 'What if my students don’t have a drum kit at home?',
  a: 'Volume 5 and the Home Practice System bonus include quiet practice-pad routines, weekly assignment sheets and 30-day challenges designed for students who only have a pad at home.'
}, {
  q: 'Can I really prepare a lesson in 5 minutes?',
  a: 'DRUM READY™ is designed to make preparation much faster. You open a volume, pick a ready-to-teach lesson and read through it in about five minutes. Time can vary with the lesson, the student and how much you adapt it.'
}, {
  q: 'Does it cover a whole studio year?',
  a: 'Yes. Volume 1 includes a 36-week studio year plan for three age tracks and 12 lessons planned minute by minute, and the other volumes give you material for every week. You choose, combine and adapt it to each student.'
}, {
  q: 'Are there worksheets in different levels?',
  a: 'Yes. Worksheets come in 3 levels, so students at different stages can work on the same concept. Answer keys are included.'
}, {
  q: 'Is assessment included?',
  a: 'Yes. Volume 6 includes level-up criteria, Test A/B per level, rubrics, term progress reports, 100 ready-made lesson and report comments, a studio policy template and an end-of-year review.'
}, {
  q: 'Are the five bonuses included?',
  a: `Yes. All five bonuses are included at no extra cost – everything for ${PRICE}.`
}, {
  q: 'Is this a subscription?',
  a: 'No. You pay once and get access to the digital material. No recurring charges.'
}, {
  q: 'Can I use it on my phone or tablet?',
  a: 'Yes. The PDF files open on computers, tablets and phones. A tablet or computer is best for viewing worksheets before printing.'
}, {
  q: 'Is there a guarantee?',
  a: 'Yes. DRUM READY™ comes with a 7-day money-back guarantee. If the material doesn’t meet your expectations, contact us within 7 days of your purchase and ask for a refund, according to the guarantee terms.'
}];
export const footerLinks = ['Terms of purchase', 'Privacy', 'Refunds', 'Contact'];
